import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanTemplates = ({ onApplyTemplate, onSkip, existingPlans }) => {
  const templates = [
    {
      id: 'basic',
      planName: 'Basic Membership',
      description: 'Perfect for getting started with your fitness journey',
      billingCycle: 'monthly',
      price: 29.99,
      setupFee: 0,
      trialPeriodDays: 7,
      features: ['Gym access during off-peak hours', 'Basic equipment access', 'Locker room access'],
      benefits: ['24/7 gym access', 'Free fitness assessment', 'Mobile app access'],
      accessLevel: 'basic',
      classCredits: 4,
      personalTrainingSessions: 0,
      icon: 'Zap',
      color: 'var(--color-muted-foreground)'
    },
    {
      id: 'premium',
      planName: 'Premium Membership',
      description: 'Most popular plan with unlimited classes and premium amenities',
      billingCycle: 'monthly',
      price: 59.99,
      setupFee: 25,
      trialPeriodDays: 14,
      features: ['Unlimited gym access', 'Unlimited group classes', 'Premium equipment access', 'Sauna and steam room'],
      benefits: ['All Basic benefits', 'Unlimited group classes', 'Guest passes (2/month)', 'Nutrition consultation'],
      accessLevel: 'premium',
      classCredits: -1,
      personalTrainingSessions: 2,
      icon: 'Star',
      color: 'var(--color-primary)'
    },
    {
      id: 'elite',
      planName: 'Elite Membership',
      description: 'Ultimate fitness experience with personal training and VIP amenities',
      billingCycle: 'monthly',
      price: 99.99,
      setupFee: 50,
      trialPeriodDays: 14,
      features: ['All Premium features', 'Personal training sessions', 'VIP locker', 'Towel service', 'Priority class booking'],
      benefits: ['All Premium benefits', '4 personal training sessions/month', 'VIP locker access', 'Complimentary towel service', 'Priority support'],
      accessLevel: 'elite',
      classCredits: -1,
      personalTrainingSessions: 4,
      icon: 'Crown',
      color: 'var(--color-accent)'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
          Choose a Template or Start from Scratch
        </h2>
        <p className="text-muted-foreground">
          Select a pre-built template to get started quickly, or skip to create a custom plan
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {templates?.map((template) => (
          <div
            key={template?.id}
            className="bg-gradient-to-br from-card to-muted/30 rounded-xl border-2 border-border p-6 hover:border-primary transition-all hover:shadow-lg cursor-pointer"
            onClick={() => onApplyTemplate(template)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name={template?.icon} size={24} color={template?.color} />
              </div>
              {template?.id === 'premium' && (
                <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
                  Popular
                </span>
              )}
            </div>
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              {template?.planName}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{template?.description}</p>
            <div className="mb-4">
              <p className="text-3xl font-bold text-primary">
                ${template?.price}
                <span className="text-sm text-muted-foreground font-normal">/month</span>
              </p>
              {template?.trialPeriodDays > 0 && (
                <p className="text-xs text-success mt-1">
                  {template?.trialPeriodDays}-day free trial
                </p>
              )}
            </div>
            <div className="space-y-2 mb-4">
              {template?.features?.slice(0, 3)?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-foreground">{feature}</span>
                </div>
              ))}
              {template?.features?.length > 3 && (
                <p className="text-xs text-muted-foreground">+{template?.features?.length - 3} more features</p>
              )}
            </div>
            <Button variant="outline" fullWidth size="sm">
              Use Template
            </Button>
          </div>
        ))}
      </div>

      {/* Existing Plans Reference */}
      {existingPlans?.length > 0 && (
        <div className="mt-8 p-4 bg-muted/50 rounded-lg">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Info" size={16} color="var(--color-primary)" />
            Your Existing Plans
          </h3>
          <div className="flex flex-wrap gap-2">
            {existingPlans?.map((plan) => (
              <span
                key={plan?.id}
                className="px-3 py-1 bg-card border border-border rounded-full text-xs text-foreground"
              >
                {plan?.plan_name} - ${plan?.price}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-center pt-6 border-t border-border">
        <Button variant="ghost" onClick={onSkip} iconName="ArrowRight" iconPosition="right">
          Skip and Create Custom Plan
        </Button>
      </div>
    </div>
  );
};

export default PlanTemplates;